
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useSupervisorSync = (goals: any[], userId?: string) => {
  useEffect(() => {
    if (!goals || goals.length === 0) return;

    const syncSupervisorIds = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          console.error("Error getting current user:", userError);
          return;
        }

        // For Personal Goals (userId is undefined): Find the user's supervisor
        if (!userId) {
          // Get current user's supervisor_id from users table
          const { data: currentUserData, error: currentUserError } = await supabase
            .from('users')
            .select('supervisor_id')
            .eq('id', user.id)
            .single();

          if (currentUserError) {
            console.error("Error fetching current user data:", currentUserError);
            return;
          }

          const supervisorId = currentUserData?.supervisor_id;
          
          if (supervisorId) {
            // Get supervisor's full_name
            const { data: supervisorData, error: supervisorError } = await supabase
              .from('users')
              .select('full_name')
              .eq('id', supervisorId)
              .single();

            if (supervisorError) {
              console.error("Error fetching supervisor data:", supervisorError);
              return;
            }

            const supervisorName = supervisorData?.full_name;

            // Update all goals for the current user with their supervisor_id
            const goalIds = goals.map(goal => goal.id);
            
            // First update the performance_goals table
            const { error: goalsUpdateError } = await supabase
              .from('performance_goals')
              .update({ supervisor_id: supervisorId })
              .in('id', goalIds);

            if (goalsUpdateError) {
              console.error("Error updating supervisor_id for personal goals:", goalsUpdateError);
            }

            // Then update performance_appraisals table for these goals with both supervisor_id and supervisor_name
            const { error: appraisalsUpdateError } = await supabase
              .from('performance_appraisals')
              .update({ 
                supervisor_id: supervisorId,
                supervisor_name: supervisorName 
              })
              .eq('user_id', user.id)
              .in('goal_id', goalIds);

            if (appraisalsUpdateError) {
              console.error("Error updating supervisor_id and supervisor_name in performance_appraisals for personal goals:", appraisalsUpdateError);
            } else {
              console.log(`Updated supervisor_id and supervisor_name in performance_appraisals for ${goalIds.length} personal goals`);
            }
          }
        } else {
          // For Manage Team Members: Current user is the supervisor
          // Get current user's full_name
          const { data: currentUserData, error: currentUserError } = await supabase
            .from('users')
            .select('full_name')
            .eq('id', user.id)
            .single();

          if (currentUserError) {
            console.error("Error fetching current user data:", currentUserError);
            return;
          }

          const supervisorName = currentUserData?.full_name;
          const goalIds = goals.map(goal => goal.id);
          
          // Update performance_goals table
          const { error: goalsUpdateError } = await supabase
            .from('performance_goals')
            .update({ supervisor_id: user.id })
            .in('id', goalIds);

          if (goalsUpdateError) {
            console.error("Error updating supervisor_id for team member goals:", goalsUpdateError);
          }

          // Update performance_appraisals table for the selected team member's goals with both supervisor_id and supervisor_name
          const { error: appraisalsUpdateError } = await supabase
            .from('performance_appraisals')
            .update({ 
              supervisor_id: user.id,
              supervisor_name: supervisorName 
            })
            .eq('user_id', userId)
            .in('goal_id', goalIds);

          if (appraisalsUpdateError) {
            console.error("Error updating supervisor_id and supervisor_name in performance_appraisals for team member goals:", appraisalsUpdateError);
          } else {
            console.log(`Updated supervisor_id and supervisor_name in performance_appraisals for ${goalIds.length} team member goals`);
          }
        }
      } catch (error) {
        console.error("Error in supervisor sync:", error);
      }
    };

    syncSupervisorIds();
  }, [goals, userId]);
};
